import React, { Component, useCallback, useEffect, useState } from 'react'
import TextEditorContainer from 'redux/containers/components/shared/texteditor/TextEditorContainer'
import PropTypes from 'prop-types'
import { USER_ANNOTATIONS_PUBLISHER } from 'constants/user-roles'
import notificationToast from 'helpers/notificationToast'
import ClockIcon from '@material-ui/icons/AccessTime'
import { toast } from 'react-toastify'
import { upsertComment } from 'services/CommentsService'
import debug from 'front-end-core/core/debug'
import ErrorIcon from '@material-ui/icons/Error'
import CheckIcon from '@material-ui/icons/CheckCircle'
import ConfirmModal from 'components/shared/ConfirmModal'
import { notifyRender } from 'front-end-core/services/WindowService'
import { updateBreakpoints } from 'services/AccordionService'
import Icon from 'components/shared/Icon'
import Grid from '@material-ui/core/Grid'
import cx from 'classnames'
import { translate } from 'components/comments/CommentWrapper'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import TealToolTip from 'components/TealToolTip'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import KeyIcon from '@material-ui/icons/VpnKey'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import CommentSpeedDial from 'components/comments/CommentSpeedDial'
import produce from 'immer'
import { DATE_FORMAT, LONG_DATE_TIME_FORMAT, TIME_FORMAT } from 'constants/locale'
import { catchError, NEVER, timer } from 'rxjs'

dayjs.extend(relativeTime)

const CommentEditor = ({
  active = true,
  body = '',
  canEdit = true,
  canArchive = false,
  canChangePrivacy = false,
  decryptionStatus = 'ok',
  newComment = false,
  partyName = '',
  partyRole = '',
  title = '',
  privacy = 'private',
  commentCategory,
  commentReference,
  commentTarget,
  id,
  userIs,
  pageToggleOverlay,
  elementId,
  dateCreated,
  lastModified,
  commentLink,
  show,
  aol
}) => {
  const [state, setState] = useState({
    body: body || '',
    decryptionStatus,
    editing: false,
    newComment,
    partyName,
    partyRole,
    privacy,
    status: 'ready',
    title: title || ''
  })

  const handleOnEdit = () => {
    setState(state => produce(state, draft => {
      draft.editing = true
    }))
  }

  const handleOnSubmit = () => {
    if (!state.body && !state.title) {
      return handleOnCancel()
    }

    setState(state => produce(state, draft => {
      draft.status = 'sending'
      draft.newComment = true
    }))

    const notificationId = notificationToast(<ClockIcon />, toast.TYPE.INFO, 'Saving comment', '', false)

    upsertComment({
      active: true,
      body: state.body,
      commentCategory,
      commentReference,
      commentTarget,
      id,
      privacy: userIs(USER_ANNOTATIONS_PUBLISHER) ? state.privacy : privacy,
      title: state.title,
      aol
    }).pipe(
      catchError(e => {
        debug.warn(e)
        setState(state => produce(state, draft => {
          draft.status = 'error'
          draft.editing = true
        }))
        toast.dismiss(notificationId)
        notificationToast(<ErrorIcon />, toast.TYPE.ERROR, 'Unable to save, please try again', 'bg-indian-red white')
        return NEVER
      })
    )
      .subscribe(() => {
        setState(state => produce(state, draft => {
          draft.editing = false
          draft.status = 'ready'
        }))
        toast.dismiss(notificationId)
        notificationToast(<CheckIcon />, toast.TYPE.SUCCESS, 'Comment saved successfully', 'bg-dark-sea-green white')
      })
  }

  const handleOnCancel = () => {
    setState(state => produce(state, draft => {
      draft.editing = false
      draft.status = 'ready'
      draft.title = title
      draft.body = body
      draft.privacy = privacy
    }))
  }

  const handleOnChangePrivacy = ({ target: { value } }) => {
    if (canChangePrivacy) {
      setState(state => produce(state, draft => {
        draft.privacy = value
      }))
    }
  }

  useEffect(() => {
    notifyRender()
    updateBreakpoints()
    // need this to make sure we don't infinite loop
    if (!state.editing) {
      setState(state => produce(state, draft => {
        draft.title = title
      }))
    }
  }, [title, state.editing])

  useEffect(() => {
    notifyRender()
    updateBreakpoints()
    // need this to make sure we don't infinite loop
    if (!state.editing) {
      setState(state => produce(state, draft => {
        draft.body = body
      }))
    }
  }, [body, state.editing])

  useEffect(() => {
    notifyRender()
    updateBreakpoints()
    // need this to make sure we don't infinite loop
    if (!state.editing) {
      setState(state => produce(state, draft => {
        draft.privacy = privacy
      }))
    }
  }, [privacy, state.privacy])

  useEffect(() => {
    notifyRender()
    updateBreakpoints()
    // I'm really not sure why we're not just using the prop here directly but this file is like a billion lines and I dare not change too much.
    if (state.decryptionStatus !== 'ok' && state.decryptionStatus !== decryptionStatus) {
      // using the function form here helps with issues with asynchronous updates - not sure if it actually causes problems but its in keeping with the previous implementation
      setState(state => produce(state, draft => {
        draft.decryptionStatus = state.decryptionStatus === 'ok' ? 'ok' : decryptionStatus
      }))
    }
  }, [decryptionStatus, state.decryptionStatus])

  if (!state.editing && ((state.newComment && (state.title || state.body)) || !state.newComment)) {
    return (
      <Comment
        {...{
          id,
          active,
          elementId,
          body: state.body,
          canEdit,
          canArchive,
          commentCategory,
          commentReference,
          commentTarget,
          dateCreated,
          lastModified,
          decryptionStatus: state.decryptionStatus,
          partyName,
          partyRole,
          title: state.title,
          privacy: state.privacy,
          handleOnCancel,
          pageToggleOverlay,
          setState,
          aol
        }}
        onEdit={handleOnEdit}
        link={commentLink}
      />
    )
  }

  return (
    <div id={elementId} className='pt6 w-100'>
      {!state.editing && state.newComment
        ? <AddNewCommentButton {...{ show, elementId }} onEdit={handleOnEdit} />
        : (
          <div
            className='ba2 pa6 pb4 transition-all'
            style={{
              borderColor: `rgba(${79}, ${127}, ${193}, ${0.2})`,
              background: `rgba(${79}, ${127}, ${193}, ${0.05})`
            }}
          >
            {!state.newComment && (
              <CommentHeader {...{ active, partyName }} privacy={state.privacy} date={dateCreated} link={commentLink} />
            )}
            <div className='w-100 dim-grey outline-0'>
              <input
                className='w-100 fw6 f4 ma0 pa4 bg-white b--phantom-grey ba'
                type='text'
                value={state.title}
                onChange={({ target: { value } }) => {
                  setState(state => produce(state, draft => {
                    draft.title = value
                  }))
                }}
                placeholder='Title...'
              />
            </div>
            <div className='comment-body mv6'>
              <TextEditorContainer
                placeholder='Content...'
                value={state.body}
                onChange={(body) => {
                  setState(state => produce(state, draft => {
                    draft.body = body
                  }))
                }}
              />
            </div>
            <CommentControls
              privacy={state.privacy}
              onPrivacyChange={handleOnChangePrivacy}
              disabled={state.status === 'sending'}
              canEdit
              onSubmit={handleOnSubmit}
              onCancel={handleOnCancel}
            />
          </div>
          )}
    </div>
  )
}

const AddNewCommentButton = ({ onEdit, show, elementId }) => (
  <div className={cx('comment-box comment-new', { 'comment-hide': !show })}>
    <Button
      variant='contained'
      className='white f6 outline-0 ttn'
      color='primary'
      classes={{ containedPrimary: 'bg-teal hover-bg-teal-darken-5' }}
      onClick={onEdit}
      id={elementId + '/button'}
    >
      <div className='flex items-center justify-between'>
        <span className='ml1 mr4'>New comment</span>
        <AddCircleOutlineIcon />
      </div>
    </Button>
  </div>
)

const Comment = ({ decryptionStatus, handleOnCancel, setState, aol, ...props }) => {
  const onArchive = useCallback(() => {
    const { body, commentCategory, commentReference, commentTarget, id, title, active, privacy, pageToggleOverlay } = props

    const onConfirmAction = () => {
      upsertComment({
        body,
        commentCategory,
        commentReference,
        commentTarget,
        id,
        title,
        active: !active,
        privacy,
        aol
      })
        .pipe(
          catchError(e => {
            debug.warn(e)
            setState(state => produce(state, draft => {
              draft.status = 'error'
              draft.editing = true
            }))
            notificationToast(<ErrorIcon />, toast.TYPE.ERROR, 'Unable to archive, please try again', 'bg-indian-red white')
            return NEVER
          })
        )
        .subscribe(() => {
          notificationToast(<CheckIcon />, toast.TYPE.SUCCESS, 'Comment archived successfully', 'bg-dark-sea-green white')
          pageToggleOverlay(null)
          handleOnCancel()
        })
    }

    pageToggleOverlay(
      <ConfirmModal
        message={
          active
            ? 'Are you sure you want to archive the comment?'
            : 'Are you sure you want to retrieve the comment?'
        }
        confirmText='Yes'
        cancelText='No'
        confirmAction={onConfirmAction}
      />
    )
  }, [props])

  switch (decryptionStatus) {
    case 'pending':
      return <PendingComment {...{ ...props, onArchive }} />
    case 'failed':
      return <FailedComment {...{ ...props, onArchive }} />
    case 'ok':
    default:
      return <DefaultComment {...{ ...props, onArchive }} />
  }
}

const FailedComment = ({ id, ...props }) => (
  <div className='comment-box default' id={id}>
    <CommentHeader {...props} />
    <div className='comment-body'>
      <KeyIcon />
      <em>Unable to decrypt comment</em>
    </div>
  </div>
)

const PendingComment = ({ canEdit, ...props }) => (
  <div className='comment-box default' id={props.id}>
    <CommentHeader canEdit={false} {...props} />
    <div className='comment-body'>
      <KeyIcon />
      <em>Decrypting...</em>
    </div>
  </div>
)

const DefaultComment = ({ id, title, body, ...props }) => (
  <div className='min-h12' id={id}>
    <CommentHeader {...props} />
    <section className='ph6 pt4 pb6'>
      {title && <div className='f4 fw6'>{title}</div>}
      <div className='pt6 f5 normal'>
        <div className='content-view' dangerouslySetInnerHTML={{ __html: body }} />
      </div>
    </section>
  </div>
)

const CommentHeader = ({ active, privacy, link, ...props }) => {
  return (
    <Grid container alignItems='center' className='f6 bg-ice-blue normal ph6 pv5 relative'>
      {/*
        Legacy feature that was not visible, see below
        {link && <CommentInfoLink {...{link}} />}
      */}
      {!active && <CommentInfoInactive />}
      {privacy && <PrivateCommentLabel {...{ privacy }} />}
      <br />
      <CommentPartyDetails {...props} />
      <CommentActionMenu {...{ active }} {...props} />
    </Grid>
  )
}

const CommentInfoInactive = () => (
  <Grid item>
    <span className='mr4'>Archived</span>
  </Grid>
)

const PrivateCommentLabel = ({ privacy }) => (
  <Grid item>
    <span
      className={cx(
        'white ph4 pv1 br2',
        { 'bg-teal': privacy === 'private' },
        { 'bg-dark-sea-green-2': privacy === 'public' }
      )}
    >
      {translate(privacy)} comment
    </span>
  </Grid>
)

const CommentPartyDetails = ({ partyName, dateCreated, lastModified }) => (
  <Grid item>
    <Grid container spacing={1}>
      {partyName && (
        <Grid item>
          <span className='ml4'>{partyName.concat(',')}</span>
        </Grid>
      )}
      {dateCreated && (
        <Grid item className='flex items-center'>
          <span className='mr2'>
            {dateCreated}
          </span>
          {dateCreated !== lastModified && <LastModified key={lastModified} time={lastModified} />}
        </Grid>
      )}
    </Grid>
  </Grid>
)

const CommentActionMenu = ({ canEdit = false, canArchive = false, onEdit, active, onArchive }) => (
  <Grid item>
    <CommentSpeedDial
      actions={[
        canEdit && { label: 'Edit', action: onEdit },
        canArchive && {
          label: active ? 'Archive' : 'Retrieve',
          action: onArchive
        }
      ]}
    />
  </Grid>
)

const CommentControls = ({ disabled = false, canEdit, onSubmit, onCancel, privacy, onPrivacyChange }) => canEdit && (
  <Grid
    container
    spacing={2}
    alignItems='center'
    justify='space-between'
    className={cx('f6 fw4 ph8 ', { disabled })}
  >
    <Grid item xs={12} sm={12} md={7} className='mb4 flex justify-center'>
      <div className='w-100'>
        {privacy && onPrivacyChange && <PrivacyControls {...{ privacy, onPrivacyChange }} />}
      </div>
    </Grid>
    <Grid item xs={12} sm={12} md={5} className='mb4'>
      <CommentButtons {...{ onSubmit, onCancel, disabled }} />
    </Grid>
  </Grid>
)

const PrivacyControls = ({ privacy, onPrivacyChange }) => (
  <Paper className='bg-white ph4'>
    <Grid container spacing={2}>
      <Grid item>
        <span>Privacy settings</span>
      </Grid>
      <Grid item className='flex items-center'>
        <PrivacyCheckbox
          option='private'
          value={privacy}
          onChange={onPrivacyChange}
        />
        <PrivacyCheckbox
          option='public'
          value={privacy}
          onChange={onPrivacyChange}
        />
      </Grid>
    </Grid>
  </Paper>
)

const CommentButtons = ({ onSubmit, onCancel, disabled }) => (
  <Grid container spacing={2}>
    {onSubmit && <SaveCommentButton {...{ onSubmit, disabled }} />}
    {onCancel && <CancelCommentButton {...{ onCancel, disabled }} />}
  </Grid>
)

const CancelCommentButton = ({ onCancel, disabled }) => (
  <Grid item xs={6}>
    <Paper>
      <Button
        className='w-100'
        classes={{ root: 'black ttn bg-white' }}
        onClick={disabled ? undefined : onCancel}
      >
        Cancel
      </Button>
    </Paper>
  </Grid>
)

const SaveCommentButton = ({ onSubmit, disabled }) => (
  <Grid item xs={6}>
    <Paper>
      <Button
        className='w-100 teal-save-button'
        classes={{ root: 'white ttn bg-teal' }}
        onClick={disabled ? undefined : onSubmit}
      >
        Save
      </Button>
    </Paper>
  </Grid>
)

const PrivacyCheckbox = ({ value, option, onChange }) => {
  const checked = option === value

  return (
    <a
      className='flex items-center black mr4 pointer privacy-filter-link'
      onClick={() => onChange({ target: { value: option } })}
      href={null}
    >
      <Icon
        className={cx('w6', { teal: checked })}
        name={{
          'icon-checkbox-blank-thin-outline': !checked,
          'icon-checkbox-marked': checked
        }}
      />
      <span className='ml2'>
        {translate(option)}
      </span>
    </a>
  )
}

class LastModified extends Component {
  constructor (props) {
    super(props)
    this.state = this.getState(props)
  }

  componentDidMount () {
    this.subscription = timer(20000, 20000).subscribe(() =>
      this.setState(this.getState(this.props))
    )
  }

  componentDidUpdate ({ time }) {
    if (time !== this.props.time) {
      this.setState(this.getState(this.props))
    }
  }

  componentWillUnmount () {
    this.subscription.unsubscribe()
  }

  shouldComponentUpdate (_, nextState) {
    return this.state.display !== nextState.display
  }

  getState (props) {
    const mDate = dayjs(props.time, `${DATE_FORMAT} ${TIME_FORMAT}`)
    return {
      display:
        mDate
          .clone()
          .add({ days: 2 })
          .diff(dayjs()) > 0
          ? mDate.fromNow()
          : mDate.format(LONG_DATE_TIME_FORMAT)
    }
  }

  render () {
    return (
      <TealToolTip title={`Last updated ${this.state.display}`}>
        <span>
          <Icon
            className='vm'
            name='icon-pencil'
            style={{ color: 'rgba(0,0,0,0.5)' }}
          />
        </span>
      </TealToolTip>
    )
  }
}

export default CommentEditor

CommentEditor.propTypes = {
  commentReference: PropTypes.string.isRequired,
  commentTarget: PropTypes.string.isRequired,
  commentCategory: PropTypes.string.isRequired,
  commentLink: PropTypes.string,
  id: PropTypes.string.isRequired,
  show: PropTypes.bool
}
